/// <reference types="vite/client" />
/**
 * Generic school SEO page.
 *
 * What: Renders a marketing page for a specific school, resolved from the URL slug.
 * Why: Gives DeepThink a unique SEO-optimised page for every primary and secondary school in Singapore.
 * When: Update when section order, structured data, or content shape changes.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Chip, Container, Stack, Typography } from '@mui/material';

import type { SchoolRecord } from '@shared/schools/schoolData.js';
import type { SchoolHeritage } from '@shared/schools/schoolHeritage.js';
import type { SchoolRichHeritage } from '@shared/schools/schoolRichHeritage.js';
import { resolveSchoolVariant } from '@shared/schools/schoolVariants.js';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import LandingBackgroundAccent from '@/components/landing/LandingBackgroundAccent';
import LandingFaqSection from '@/components/landing/LandingFaqSection';
import LandingFooter from '@/components/landing/LandingFooter';
import LandingMobileNav from '@/components/landing/LandingMobileNav';
import LandingProgramFactsSection from '@/components/landing/LandingProgramFactsSection';
import LandingRelatedLinksSection from '@/components/landing/LandingRelatedLinksSection';
import { landingRevealStyles } from '@/components/landing/landingRevealStyles';
import { colors, containerPadding } from '@/components/landing/landingTokens';
import LandingTopBar from '@/components/landing/LandingTopBar';
import LandingTrialCtas from '@/components/landing/LandingTrialCtas';
import ScrollProgressBar from '@/components/landing/ScrollProgressBar';
import LevelCtaSection from '@/components/levels/LevelCtaSection';
import SchoolAboutSection from '@/components/schools/SchoolAboutSection';
import SchoolCutOffSection from '@/components/schools/SchoolCutOffSection';
import SchoolIndependenceNotice from '@/components/schools/SchoolIndependenceNotice';
import SchoolPageNav, {
  buildSchoolNavItems,
  hasSchoolPageNav,
} from '@/components/schools/SchoolPageNav';
import SchoolProfileCard from '@/components/schools/SchoolProfileCard';
import SchoolRichContent from '@/components/schools/SchoolRichContent';
import SchoolSupportSection, {
  SCHOOL_SUPPORT_SECTION_ID,
} from '@/components/schools/SchoolSupportSection';
import SchoolTrialCallout from '@/components/schools/SchoolTrialCallout';
import { getBrandingConfig } from '@/config/runtimeBrandConfig';
import { buildSchoolPageContent } from '@/content/schoolPageContent';
import { useAuth } from '@/hooks/useAuth';
import { useSectionFadeIn } from '@/hooks/useSectionFadeIn';
import {
  loadSchoolBySlug,
  loadSchoolHeritage,
  loadSchoolRichHeritage,
  SchoolDataRequestError,
} from '@/services/schoolDataApi.js';
import { getDefaultRedirectPath } from '@/utils/accessControl';
import { trackMetaEvent } from '@/utils/metaPixel.js';
import { safeJsonLd } from '@/utils/safeJsonLd';

interface SchoolPageBundle {
  school: SchoolRecord;
  heritage: SchoolHeritage | null;
  richHeritage: SchoolRichHeritage | null;
}

/**
 * Read the per-school data bundle embedded by the prerender pipeline.
 *
 * The prerender script injects a `<script id="__school-page-data__" type="application/json">`
 * tag into the page's <head> before snapshotting the HTML. Reading it synchronously seeds
 * every useState slot the page depends on — without it, hydration re-renders the page with
 * null heritage state and React removes the prerendered intro/about/sections DOM (visible
 * content flash, React errors 418/423).
 */
function readPrerenderPageBundle(slug: string): SchoolPageBundle | null {
  if (typeof document === 'undefined') return null;
  try {
    const el = document.getElementById('__school-page-data__');
    if (!el) return null;
    const data = JSON.parse(el.textContent ?? '') as SchoolPageBundle;
    return data?.school?.slug === slug ? data : null;
  } catch {
    return null;
  }
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function SchoolMathPage() {
  const { loginWithRedirect } = useAuth0();
  const { isAuthenticated, userRole, isRoleLoaded } = useAuth();
  const navigate = useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();

  const branding = getBrandingConfig();
  const brandName = branding?.siteTitle || 'DeepThink';
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : branding?.siteUrl || 'https://deepthink.sg';
  const ogImage = branding?.ogImageUrl || `${origin}/og-image.png`;

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Initialize every per-school state slot synchronously from the prerender bundle so
  // the first render matches the prerendered DOM and hydration succeeds without
  // mismatches (React errors 418/423) or content flashes from heritage-derived sections.
  const [school, setSchool] = useState<SchoolRecord | null>(
    () => readPrerenderPageBundle(slug)?.school ?? null
  );
  const [schoolLoadState, setSchoolLoadState] = useState<
    'idle' | 'loading' | 'loaded' | 'not-found' | 'error'
  >(() => (readPrerenderPageBundle(slug) !== null ? 'loaded' : 'idle'));
  const [heritage, setHeritage] = useState<SchoolHeritage | null>(
    () => readPrerenderPageBundle(slug)?.heritage ?? null
  );
  const [richHeritage, setRichHeritage] = useState<SchoolRichHeritage | null>(
    () => readPrerenderPageBundle(slug)?.richHeritage ?? null
  );

  // Scroll-triggered reveals, same system as the homepage — but only for the
  // DeepThink run. Every school section is deliberately absent, and the absence
  // is the design: `landingRevealStyles` already excludes the blog article body
  // on the principle that prose must be readable the moment it paints, and the
  // school run is exactly that kind of content. The cut-off table is the figure
  // most of this page's traffic searched for; it should not be withheld behind
  // an entrance, and neither should the ~870 words of heritage prose under it.
  //
  // It also settles a mismatch that the section reorder made legible. Every
  // school section is `Shell > Paper > Stack` — a shape `assignStaggerIndices`
  // cannot reach into — so they entered as whole blocks, directly above a
  // marketing run whose children cascade. Uniform motion was the alternative,
  // but it means widening the stagger selectors across every marketing page to
  // add motion, on a page whose complaint was that the motion read as
  // inconsistent. Withholding it instead makes the boundary carry meaning:
  // the school's own content is simply there, and the pitch below it arrives.
  //
  // The list stays empty until the page has content: the sections don't exist
  // in the DOM before then, and the identity change on load is what re-runs the
  // observer setup.
  // Listed in render order. The hook itself is order-blind, but a list that
  // drifts from the DOM is how a section quietly stops being observed.
  const animatedSectionIds = useMemo(
    () =>
      schoolLoadState === 'loaded'
        ? [
            SCHOOL_SUPPORT_SECTION_ID,
            `school-${slug}-facts`,
            `school-${slug}-faq`,
            `school-${slug}-related`,
            'cta',
          ]
        : [],
    [schoolLoadState, slug]
  );
  useSectionFadeIn(animatedSectionIds);

  // Validate slug format before data lookup (security: prevent path traversal)
  const isValidSlug = SLUG_PATTERN.test(slug);

  // Skip the initial network fetch when state was seeded from prerendered data.
  // Cleared after the first effect run so that SPA slug changes still fetch normally.
  const skipInitialFetchRef = useRef(school !== null);

  useEffect(() => {
    if (!isValidSlug) {
      setSchool(null);
      setSchoolLoadState('not-found');
      return;
    }

    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }

    let isActive = true;
    setSchool(null);
    setHeritage(null);
    setRichHeritage(null);
    setSchoolLoadState('loading');

    // Heritage is supplementary; a transient 500 / malformed JSON / network blip on
    // either heritage source must not blank the whole page. Swallow per-source errors
    // to null so only a school-record failure can move the page off 'loaded'.
    Promise.all([
      loadSchoolBySlug(slug),
      loadSchoolHeritage(slug).catch(() => null),
      loadSchoolRichHeritage(slug).catch(() => null),
    ])
      .then(([record, heritageData, richHeritageData]) => {
        if (!isActive) return;
        setSchool(record);
        setHeritage(heritageData);
        setRichHeritage(richHeritageData);
        setSchoolLoadState('loaded');
      })
      .catch(error => {
        if (!isActive) return;

        if (error instanceof SchoolDataRequestError && error.status === 404) {
          setSchool(null);
          setSchoolLoadState('not-found');
          return;
        }

        console.error(`Failed to load school data for slug "${slug}"`, error);
        setSchool(null);
        setSchoolLoadState('error');
      });

    return () => {
      isActive = false;
    };
  }, [isValidSlug, slug]);

  // Redirect unknown slugs to the primary hub page
  useEffect(() => {
    if (schoolLoadState === 'not-found') {
      navigate('/primary-school', { replace: true });
    }
  }, [schoolLoadState, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (school) {
      trackMetaEvent('PageView', {
        content_name: school.displayName,
        page_url: `${origin}${school.routePath}`,
      });
    }
  }, [school, origin]);

  const handleLogin = () => {
    if (isAuthenticated && isRoleLoaded) {
      navigate(getDefaultRedirectPath(userRole), { replace: true });
    } else {
      loginWithRedirect();
    }
  };

  if (
    schoolLoadState === 'idle' ||
    schoolLoadState === 'loading' ||
    schoolLoadState === 'not-found'
  ) {
    return <LoadingSpinner minHeight="100vh" />;
  }

  if (!school || schoolLoadState === 'error') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          backgroundColor: colors.background,
        }}
      >
        <Typography
          sx={{
            fontSize: '1rem',
            color: colors.inkMuted,
            textAlign: 'center',
            maxWidth: 420,
          }}
        >
          Unable to load this school page right now.
        </Typography>
      </Box>
    );
  }

  const variant = resolveSchoolVariant(school);
  const content = buildSchoolPageContent(school, variant, heritage);
  const canonicalUrl = `${origin}${school.routePath}`;

  // The school's own prose is the bulk of this page and all of it renders below
  // the fold, so the nav advertises it and the mid-page callout can promise it.
  // Both are withheld when heritage failed to load rather than pointing at
  // sections that aren't there.
  const navItems = buildSchoolNavItems({
    hasCutOff: content.cutOffPoints !== undefined,
    hasAbout: content.aboutSchool !== undefined,
    richHeritage,
  });
  const showNav = hasSchoolPageNav(navItems);
  const hasSchoolContentBelow = (richHeritage?.sections.length ?? 0) > 0;
  const isProduction = import.meta.env.MODE === 'production';
  const robotsContent = isProduction
    ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
    : 'noindex,nofollow';

  // --- Structured data ---
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: origin },
      {
        '@type': 'ListItem',
        position: 2,
        name: school.servesPrimary ? 'Primary School' : 'Secondary School',
        item: `${origin}${school.servesPrimary ? '/primary-school' : '/secondary-school'}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: school.displayName,
        item: canonicalUrl,
      },
    ],
  };

  const educationalOrgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: school.displayName,
    alternateName: school.abbreviation || undefined,
    url: /^https?:\/\//i.test(school.websiteUrl ?? '')
      ? school.websiteUrl
      : undefined,
    address: school.address
      ? {
          '@type': 'PostalAddress',
          streetAddress: school.address,
          postalCode: school.postalCode,
          addressCountry: 'SG',
        }
      : undefined,
    telephone: school.phone || undefined,
    email: school.email || undefined,
  };

  // Merge templated FAQs (tuition-focused) with rich heritage FAQs (school-specific).
  // Both render on the page — templated in LandingFaqSection, heritage in SchoolRichContent —
  // so including both in the FAQPage schema satisfies Google's "all marked-up FAQs must be
  // visible" rule and gives every school a unique structured-data fingerprint.
  const mergedFaqEntries = [...content.faqItems, ...(richHeritage?.faqs ?? [])];
  const faqJsonLd =
    mergedFaqEntries.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: mergedFaqEntries.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
          })),
        }
      : null;

  return (
    <>
      <Helmet>
        <title>{content.metaTitle}</title>
        <meta name="description" content={content.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={content.metaTitle} />
        <meta property="og:description" content={content.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content={`${brandName} – ${school.displayName}`}
        />
        <meta property="og:site_name" content={brandName} />
        <meta property="og:locale" content="en_SG" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content.metaTitle} />
        <meta name="twitter:description" content={content.metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta
          name="twitter:image:alt"
          content={`${brandName} – ${school.displayName}`}
        />
        <meta name="theme-color" content="#F5F7FC" />
        <meta name="robots" content={robotsContent} />
        <script type="application/ld+json">
          {safeJsonLd(breadcrumbJsonLd)}
        </script>
        <script type="application/ld+json">
          {safeJsonLd(educationalOrgJsonLd)}
        </script>
        {faqJsonLd && (
          <script type="application/ld+json">{safeJsonLd(faqJsonLd)}</script>
        )}
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.background,
          position: 'relative',
          overflow: 'hidden',
          // Scroll-reveal animations — see landingRevealStyles.ts for the
          // design system (motion types, intensity tiers, section mapping).
          ...(landingRevealStyles as Record<string, unknown>),
        }}
      >
        <LandingBackgroundAccent />
        <ScrollProgressBar />

        <LandingTopBar
          onLogin={handleLogin}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
        <LandingMobileNav
          onLogin={handleLogin}
          brandName={brandName}
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />

        <Box
          component="main"
          sx={{
            position: 'relative',
            zIndex: 1,
            pt: { xs: '80px', sm: '88px', md: '96px' },
          }}
        >
          {/* Hero */}
          <Container
            maxWidth="lg"
            disableGutters
            sx={{ px: containerPadding.px }}
          >
            {/*
              The nav belongs to the hero, so where one renders the header
              closes tighter and the nav opens the gap to the profile card
              instead. `md` is deliberately tighter than `sm`: the nav is
              desktop-only, so below `md` this padding is still the hero's own
              gap to the profile card and has to keep its original value.
            */}
            <Box
              component="header"
              sx={{
                pt: { xs: 5, sm: 7, md: 8 },
                pb: showNav ? { xs: 4, sm: 5, md: 4 } : { xs: 4, sm: 5, md: 6 },
              }}
            >
              <Stack spacing={{ xs: 2.5, sm: 3 }} sx={{ maxWidth: 760 }}>
                <Chip
                  label={content.levelBadge}
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    backgroundColor: colors.washAmber,
                    // Orange-on-amber-wash needs the dark step to clear AA.
                    color: colors.accentOrangeDark,
                    letterSpacing: '0.04em',
                    height: 28,
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '2.875rem' },
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: '-0.03em',
                    color: colors.ink,
                  }}
                >
                  {content.heroHeading}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '1.0625rem', sm: '1.125rem' },
                    color: colors.inkMuted,
                    lineHeight: 1.65,
                    maxWidth: 600,
                  }}
                >
                  {content.heroSubheading}
                </Typography>
                {content.introText && (
                  <Typography
                    sx={{
                      fontSize: { xs: '0.9375rem', sm: '1rem' },
                      color: colors.inkMuted,
                      lineHeight: 1.7,
                      maxWidth: 640,
                    }}
                  >
                    {content.introText}
                  </Typography>
                )}
                <LandingTrialCtas
                  sourceLocation={`school-${slug}-hero`}
                  sx={{ pt: { xs: 0.5, sm: 1 } }}
                />
              </Stack>
            </Box>

            <SchoolIndependenceNotice schoolName={school.displayName} />

            <SchoolPageNav items={navItems} />
          </Container>

          {/*
            Section order: every school section first, then every DeepThink
            section, then the outbound links.

            The school's own content used to be split in two by the branded
            band, which put ~870 words of school prose and ten school FAQs below
            four consecutive DeepThink sections — a reader who stopped at the
            branded band did so believing the page had ended. Keeping the school
            run contiguous removes that false floor. The link grid moved to the
            end for the same reason: eight to sixteen ways off the page belong
            after the reader is done, not in the middle of their read.
          */}
          <Container
            maxWidth="lg"
            disableGutters
            sx={{ px: containerPadding.px }}
          >
            <Stack spacing={{ xs: 4, sm: 5, md: 6 }}>
              <SchoolProfileCard school={school} />
              {/* The cut-off is what the searcher came for, so it sits above
                  the heritage and tuition sections. Absent for every school MOE
                  publishes no cut-off point for — never an empty state. */}
              {content.cutOffPoints && (
                <SchoolCutOffSection content={content.cutOffPoints} />
              )}
              {content.aboutSchool && (
                <SchoolAboutSection content={content.aboutSchool} />
              )}
              {/* The one offer inside the school run — placed where the reader
                  has what they came for, and only where there is still school
                  content below for its closing line to point at. */}
              {hasSchoolContentBelow && (
                <SchoolTrialCallout
                  schoolName={school.displayName}
                  sourceLocation={`school-${slug}-midpage`}
                />
              )}
              <SchoolRichContent data={richHeritage} />
              <SchoolSupportSection
                heading={content.supportHeading}
                bullets={content.supportBullets}
              />
              <LandingProgramFactsSection
                id={`school-${slug}-facts`}
                content={content.programFacts}
                tone="muted"
                align="center"
              />
              <LandingFaqSection
                id={`school-${slug}-faq`}
                content={content.faqItems}
                tone="transparent"
                align="center"
              />
              <LandingRelatedLinksSection
                id={`school-${slug}-related`}
                content={content.relatedLinks}
                tone="transparent"
                align="center"
              />
            </Stack>
          </Container>

          {/* CTA */}
          <Box sx={{ mt: { xs: 6, sm: 8, md: 10 } }}>
            <LevelCtaSection
              levelId={`school-${slug}`}
              sourceLocation={`school-${slug}`}
            />
          </Box>

          <LandingFooter />
        </Box>
      </Box>
    </>
  );
}

export default SchoolMathPage;
